import React from 'react';
import { View, Text, Pressable, TextInput } from 'react-native';
import {
  DollarSign,
  TrendingUp,
  CheckCircle,
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Rocket,
  ShieldCheck,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { CommitmentProgress } from '@/components/CommitmentProgress';
import { formatCurrency } from '@/lib/utils/format';
import { capitalizeWords } from '@/lib/format';
import type { FundraisingRound } from '@/lib/state/profile-store';
import type { ExternalCommitment, CommitmentSummary, CommitmentSettings } from '@/lib/state/dealflow-store';

const stageLabels: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
};

interface VerifiedCommitment {
  id: string;
  investorName: string;
  investorFirm?: string;
  amount: number;
  type: 'soft' | 'hard';
  status: string;
}

interface FundraisingSectionProps {
  isActivelyRaising: boolean;
  onToggleRaising: () => void;
  commitmentSummary: CommitmentSummary | null;
  onCloseRound: () => void;
  // Visibility settings
  commitmentSettings: CommitmentSettings;
  onSetCommitmentSettings: (settings: Partial<CommitmentSettings>) => void;
  // External commitments
  externalCommitments: ExternalCommitment[];
  showAddExternal: boolean;
  onToggleAddExternal: () => void;
  editingExternalId: string | null;
  onSetEditingExternalId: (id: string | null) => void;
  // External form state
  extName: string;
  onSetExtName: (v: string) => void;
  extFirm: string;
  onSetExtFirm: (v: string) => void;
  extAmount: string;
  onSetExtAmount: (v: string) => void;
  extType: 'soft' | 'hard';
  onSetExtType: (v: 'soft' | 'hard') => void;
  extNotes: string;
  onSetExtNotes: (v: string) => void;
  onAddExternal: () => void;
  onEditExternal: (ec: ExternalCommitment) => void;
  onRemoveExternal: (id: string) => void;
  // Verified commitments
  verifiedCommitments: VerifiedCommitment[];
  // Round history
  fundraisingHistory?: FundraisingRound[];
  onStartNewRound: () => void;
  formatDate: (dateString: string) => string;
}

export function FundraisingSection({
  isActivelyRaising,
  onToggleRaising,
  commitmentSummary,
  onCloseRound,
  commitmentSettings,
  onSetCommitmentSettings,
  externalCommitments,
  showAddExternal,
  onToggleAddExternal,
  editingExternalId,
  onSetEditingExternalId,
  extName,
  onSetExtName,
  extFirm,
  onSetExtFirm,
  extAmount,
  onSetExtAmount,
  extType,
  onSetExtType,
  extNotes,
  onSetExtNotes,
  onAddExternal,
  onEditExternal,
  onRemoveExternal,
  verifiedCommitments,
  fundraisingHistory,
  onStartNewRound,
  formatDate,
}: FundraisingSectionProps) {
  const colors = useThemeColors();

  return (
    <View>
      {/* Actively Raising toggle */}
      <Pressable
        onPress={onToggleRaising}
        accessibilityRole="switch"
        accessibilityLabel="Actively raising"
        accessibilityState={{ checked: isActivelyRaising }}
        className="flex-row items-center justify-between p-4 rounded-2xl mb-4"
        style={{
          backgroundColor: isActivelyRaising ? `${colors.primary[500]}10` : colors.surface.secondary,
          borderWidth: 1,
          borderColor: isActivelyRaising ? `${colors.primary[500]}30` : colors.border.light,
        }}
      >
        <View className="flex-row items-center gap-2.5">
          <DollarSign size={16} color={isActivelyRaising ? colors.primary[500] : colors.text.tertiary} />
          <View>
            <Text className="text-sm font-medium" style={{ color: colors.text.primary }}>
              Actively Raising
            </Text>
            <Text className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>
              {isActivelyRaising ? 'Fundraising features enabled' : 'Enable to track commitments'}
            </Text>
          </View>
        </View>
        <View className="w-10 h-6 rounded-full justify-center px-0.5" style={{
          backgroundColor: isActivelyRaising ? colors.primary[500] : colors.border.medium,
        }}>
          <View className="w-5 h-5 rounded-full bg-white" style={{
            alignSelf: isActivelyRaising ? 'flex-end' : 'flex-start',
          }} />
        </View>
      </Pressable>

      {isActivelyRaising && (
        <>
          {/* Commitment Progress */}
          {commitmentSummary && (
            <View className="mb-4">
              <CommitmentProgress commitmentSummary={commitmentSummary} />
            </View>
          )}

          {/* Close Round Button */}
          <Pressable
            onPress={onCloseRound}
            className="flex-row items-center justify-center gap-2 p-3.5 rounded-2xl mb-4 active:opacity-70"
            style={{
              borderWidth: 1.5,
              borderColor: colors.primary[500],
              backgroundColor: `${colors.primary[500]}08`,
            }}
          >
            <CheckCircle size={18} color={colors.primary[500]} />
            <Text className="text-sm font-semibold" style={{ color: colors.primary[500] }}>
              Close Round
            </Text>
          </Pressable>

          {/* Visibility Settings */}
          <View className="p-4 rounded-2xl mb-4" style={{
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}>
            <Text className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.text.tertiary }}>
              Visibility Settings
            </Text>

            <Pressable
              onPress={() => onSetCommitmentSettings({ showCommitmentProgress: !commitmentSettings.showCommitmentProgress })}
              className="flex-row items-center justify-between py-2"
            >
              <View className="flex-row items-center gap-2">
                {commitmentSettings.showCommitmentProgress ? <Eye size={14} color={colors.primary[500]} /> : <EyeOff size={14} color={colors.text.tertiary} />}
                <Text className="text-sm" style={{ color: colors.text.primary }}>Show commitment progress</Text>
              </View>
              <View className="w-10 h-6 rounded-full justify-center px-0.5" style={{
                backgroundColor: commitmentSettings.showCommitmentProgress ? colors.primary[500] : colors.border.medium,
              }}>
                <View className="w-5 h-5 rounded-full bg-white" style={{
                  alignSelf: commitmentSettings.showCommitmentProgress ? 'flex-end' : 'flex-start',
                }} />
              </View>
            </Pressable>

            {commitmentSettings.showCommitmentProgress && (
              <View className="ml-6 mt-1 gap-2">
                {([
                  { key: 'showInvestorCount' as const, label: 'Show investor count' },
                  { key: 'showAmounts' as const, label: 'Show amounts' },
                  { key: 'showVerifiedOnly' as const, label: 'Verified only' },
                ] as const).map(({ key, label }) => (
                  <Pressable
                    key={key}
                    onPress={() => onSetCommitmentSettings({ [key]: !commitmentSettings[key] })}
                    className="flex-row items-center justify-between py-1.5"
                  >
                    <Text className="text-xs" style={{ color: colors.text.secondary }}>{label}</Text>
                    <View className="w-8 h-5 rounded-full justify-center px-0.5" style={{
                      backgroundColor: commitmentSettings[key] ? colors.primary[500] : colors.border.medium,
                    }}>
                      <View className="w-4 h-4 rounded-full bg-white" style={{
                        alignSelf: commitmentSettings[key] ? 'flex-end' : 'flex-start',
                      }} />
                    </View>
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          {/* External Commitments */}
          <View className="mb-4">
            <View className="flex-row items-center justify-between mb-3">
              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                External Commitments
              </Text>
              <Pressable
                onPress={() => { onSetEditingExternalId(null); onToggleAddExternal(); }}
                className="flex-row items-center gap-1 px-3 py-1.5 rounded-full"
                style={{ backgroundColor: `${colors.primary[500]}15` }}
              >
                <Plus size={14} color={colors.primary[500]} />
                <Text className="text-xs font-semibold" style={{ color: colors.primary[500] }}>Add</Text>
              </Pressable>
            </View>

            {/* Add/Edit Form */}
            {showAddExternal && (
              <View className="p-4 rounded-2xl mb-3" style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.primary[500] + '30',
              }}>
                <TextInput
                  value={extName}
                  onChangeText={onSetExtName}
                  placeholder="Investor name *"
                  placeholderTextColor={colors.text.tertiary}
                  className="p-3 rounded-xl mb-2 text-sm"
                  style={{ backgroundColor: colors.surface.primary, color: colors.text.primary, borderWidth: 1, borderColor: colors.border.light }}
                />
                <TextInput
                  value={extFirm}
                  onChangeText={onSetExtFirm}
                  placeholder="Firm name (optional)"
                  placeholderTextColor={colors.text.tertiary}
                  className="p-3 rounded-xl mb-2 text-sm"
                  style={{ backgroundColor: colors.surface.primary, color: colors.text.primary, borderWidth: 1, borderColor: colors.border.light }}
                />
                <TextInput
                  value={extAmount}
                  onChangeText={onSetExtAmount}
                  placeholder="Amount (USD) *"
                  placeholderTextColor={colors.text.tertiary}
                  keyboardType="numeric"
                  className="p-3 rounded-xl mb-2 text-sm"
                  style={{ backgroundColor: colors.surface.primary, color: colors.text.primary, borderWidth: 1, borderColor: colors.border.light }}
                />
                {/* Type selector */}
                <View className="flex-row gap-2 mb-2">
                  {(['soft', 'hard'] as const).map((type) => (
                    <Pressable
                      key={type}
                      onPress={() => onSetExtType(type)}
                      className="flex-1 p-2.5 rounded-xl items-center"
                      style={{
                        backgroundColor: extType === type ? `${colors.primary[500]}15` : colors.surface.primary,
                        borderWidth: 1,
                        borderColor: extType === type ? colors.primary[500] : colors.border.light,
                      }}
                    >
                      <Text className="text-xs font-semibold" style={{ color: extType === type ? colors.primary[500] : colors.text.secondary }}>
                        {type === 'soft' ? 'Soft Commit' : 'Hard Commit'}
                      </Text>
                    </Pressable>
                  ))}
                </View>
                <TextInput
                  value={extNotes}
                  onChangeText={onSetExtNotes}
                  placeholder="Notes (optional)"
                  placeholderTextColor={colors.text.tertiary}
                  multiline
                  className="p-3 rounded-xl mb-3 text-sm"
                  style={{ backgroundColor: colors.surface.primary, color: colors.text.primary, borderWidth: 1, borderColor: colors.border.light, minHeight: 60 }}
                />
                <View className="flex-row gap-2">
                  <Pressable
                    onPress={() => { onToggleAddExternal(); onSetEditingExternalId(null); }}
                    className="flex-1 p-3 rounded-xl items-center"
                    style={{ borderWidth: 1, borderColor: colors.border.medium }}
                  >
                    <Text className="text-sm font-semibold" style={{ color: colors.text.secondary }}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={onAddExternal}
                    className="flex-1 p-3 rounded-xl items-center"
                    style={{ backgroundColor: colors.primary[500] }}
                  >
                    <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                      {editingExternalId ? 'Update' : 'Add'}
                    </Text>
                  </Pressable>
                </View>
              </View>
            )}

            {/* External commitment cards */}
            {externalCommitments.map(ec => (
              <View key={ec.id} className="p-3 rounded-xl mb-2 flex-row items-center justify-between" style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}>
                <View className="flex-1 mr-3">
                  <View className="flex-row items-center gap-2">
                    <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                      {ec.investorName}
                    </Text>
                    <View className="px-1.5 py-0.5 rounded" style={{
                      backgroundColor: ec.type === 'hard' ? `${colors.success}15` : `${colors.primary[500]}15`,
                    }}>
                      <Text className="text-[9px] font-bold" style={{
                        color: ec.type === 'hard' ? colors.success : colors.primary[500],
                      }}>
                        {ec.type.toUpperCase()}
                      </Text>
                    </View>
                  </View>
                  {ec.investorFirm && (
                    <Text className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>{ec.investorFirm}</Text>
                  )}
                  <Text className="text-sm font-semibold mt-1" style={{ color: colors.text.primary }}>
                    {formatCurrency(ec.amount)}
                  </Text>
                </View>
                <View className="flex-row gap-2">
                  <Pressable onPress={() => onEditExternal(ec)} className="p-2 rounded-lg" style={{ backgroundColor: `${colors.primary[500]}10` }}>
                    <Edit3 size={14} color={colors.primary[500]} />
                  </Pressable>
                  <Pressable onPress={() => onRemoveExternal(ec.id)} className="p-2 rounded-lg" style={{ backgroundColor: `${colors.error}10` }}>
                    <Trash2 size={14} color={colors.error} />
                  </Pressable>
                </View>
              </View>
            ))}

            {externalCommitments.length === 0 && !showAddExternal && (
              <Text className="text-xs text-center py-4" style={{ color: colors.text.tertiary }}>
                No external commitments added yet
              </Text>
            )}
          </View>

          {/* Verified Commitments (read-only) */}
          {verifiedCommitments.length > 0 && (
            <View className="mb-4">
              <View className="flex-row items-center gap-2 mb-3">
                <ShieldCheck size={14} color={colors.primary[500]} />
                <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                  Verified Commitments
                </Text>
              </View>
              {verifiedCommitments.map(vc => (
                <View key={vc.id} className="p-3 rounded-xl mb-2 flex-row items-center justify-between" style={{
                  backgroundColor: `${colors.primary[500]}08`,
                  borderWidth: 1,
                  borderColor: `${colors.primary[500]}15`,
                }}>
                  <View>
                    <View className="flex-row items-center gap-2">
                      <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                        {vc.investorName}
                      </Text>
                      <View className="px-1.5 py-0.5 rounded" style={{
                        backgroundColor: vc.status === 'converted' || vc.type === 'hard' ? `${colors.success}15` : `${colors.primary[500]}15`,
                      }}>
                        <Text className="text-[9px] font-bold" style={{
                          color: vc.status === 'converted' || vc.type === 'hard' ? colors.success : colors.primary[500],
                        }}>
                          {vc.status === 'converted' || vc.type === 'hard' ? 'HARD' : 'SOFT'}
                        </Text>
                      </View>
                    </View>
                    {vc.investorFirm && (
                      <Text className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>{vc.investorFirm}</Text>
                    )}
                  </View>
                  <Text className="text-sm font-bold" style={{ color: colors.primary[500] }}>
                    {formatCurrency(vc.amount)}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </>
      )}

      {/* Round History - shows when not raising and history exists */}
      {!isActivelyRaising && (fundraisingHistory?.length ?? 0) > 0 && (() => {
        const lastRound = fundraisingHistory![fundraisingHistory!.length - 1];
        return (
          <View className="p-4 rounded-2xl" style={{
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}>
            <Text className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.text.tertiary }}>
              Last Round
            </Text>
            <View className="flex-row items-center justify-between mb-2">
              <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                {stageLabels[lastRound.stage] ?? capitalizeWords(lastRound.stage)}
              </Text>
              <Text className="text-sm font-bold" style={{ color: colors.primary[500] }}>
                {formatCurrency(lastRound.amount)}
              </Text>
            </View>
            <View className="flex-row items-center gap-3 mb-3">
              <Text className="text-xs" style={{ color: colors.text.tertiary }}>
                {formatDate(lastRound.date)}
              </Text>
              {lastRound.leadInvestor && (
                <Text className="text-xs" style={{ color: colors.text.secondary }}>
                  Lead: {lastRound.leadInvestor}
                </Text>
              )}
            </View>
            <Pressable
              onPress={onStartNewRound}
              className="flex-row items-center justify-center gap-2 p-3 rounded-xl active:opacity-70"
              style={{ backgroundColor: colors.primary[500] }}
            >
              <Rocket size={16} color="white" />
              <Text className="text-sm font-semibold text-white">Start New Round</Text>
            </Pressable>
          </View>
        );
      })()}
    </View>
  );
}
